/**
 * 260810 E2-P2 — Phần "quyết định" của khối Phụ đề AI, tách khỏi mọi thứ dính antd.
 *
 * 🔴 Vì sao phải là file riêng: `antd v6 + react-router-dom v7` không load được dưới jest của
 * react-scripts (App.test.tsx fail sẵn). Logic nào nằm trong file có `import ... from 'antd'` là
 * vĩnh viễn không test được trong repo này. Mẫu đang chạy: utils/filmLocationMatch.ts.
 */
import { SubtitleEntry, SubtitleJob, SubtitleJobEntry } from '../types';

export interface JobActions {
    /** Bấm Duyệt được không. */
    canApprove: boolean;
    /** Bấm Sinh lại được không. */
    canRegenerate: boolean;
    /** Job đang chạy — khoá nút, hiện spinner. */
    isBusy: boolean;
    /** Có cần hỏi lại trạng thái theo chu kỳ không (job đứng yên thì đừng gọi API cho tốn). */
    shouldPoll: boolean;
}

/**
 * @param cueCountOverride số cue đang có trên màn hình — admin có thể vừa xoá hết trong panel,
 *        lúc đó `entriesCount` của job (chụp lúc AI chạy xong) không còn đúng nữa.
 */
export function deriveJobActions(job: SubtitleJob | null, cueCountOverride?: number): JobActions {
    if (!job) {
        return { canApprove: false, canRegenerate: true, isBusy: false, shouldPoll: false };
    }

    const isBusy = job.status === 'pending' || job.status === 'running';
    const cueCount = cueCountOverride !== undefined ? cueCountOverride : job.entriesCount || 0;

    return {
        canApprove: job.status === 'awaiting_review' && cueCount > 0,
        canRegenerate: !isBusy,
        isBusy,
        shouldPoll: isBusy,
    };
}

export interface JobStatusView {
    label: string;
    /** Màu Tag của antd. */
    color: string;
    tone: 'idle' | 'busy' | 'ready' | 'warn' | 'done' | 'error';
}

export function describeJobStatus(job: SubtitleJob | null): JobStatusView {
    if (!job) {
        return { label: 'Chưa có phụ đề AI', color: 'default', tone: 'idle' };
    }

    switch (job.status) {
        case 'pending':
            return { label: 'Đang chờ tới lượt', color: 'blue', tone: 'busy' };
        case 'running':
            return { label: 'Đang nghe video…', color: 'processing', tone: 'busy' };
        case 'awaiting_review':
            // Cảnh báo chỉ có nghĩa TRƯỚC khi người xem — sau khi duyệt thì nó đã được xử lý.
            return job.warning
                ? { label: 'Chờ duyệt — cần kiểm lại', color: 'orange', tone: 'warn' }
                : { label: 'Chờ duyệt', color: 'gold', tone: 'ready' };
        case 'approved':
            return { label: 'Đã duyệt', color: 'green', tone: 'done' };
        case 'failed':
            return { label: 'Sinh phụ đề lỗi', color: 'red', tone: 'error' };
        default:
            return { label: job.status, color: 'default', tone: 'idle' };
    }
}

/** Cue của backend → cue mà SubtitleEditorPanel đòi (`confidence` là trường bắt buộc bên đó). */
export function toEditorEntries(entries: SubtitleJobEntry[] | null | undefined): SubtitleEntry[] {
    if (!Array.isArray(entries)) return [];
    return entries.map((e) => ({
        text: e.text,
        startTime: e.startTime,
        endTime: e.endTime,
        confidence: 1,
    }));
}

/** Ngược lại — backend không cần `confidence`, gửi thừa chỉ tổ nặng payload. */
export function fromEditorEntries(entries: SubtitleEntry[] | null | undefined): SubtitleJobEntry[] {
    if (!Array.isArray(entries)) return [];
    return entries.map((e) => ({
        text: e.text,
        startTime: e.startTime,
        endTime: e.endTime,
    }));
}

/**
 * Có nên nạp lại danh sách cue từ server không, và sau đó "khoá" ở job nào.
 *
 * 🔴 Bug 10/08 phải tránh tái diễn: bản đầu khoá ngay khi thấy job, kể cả lúc job còn ĐANG CHẠY và
 * `entries` vẫn rỗng. Job chạy xong thì `id` không đổi ⇒ nhánh nạp bị bỏ qua vĩnh viễn ⇒ màn hình
 * hiện "0 entries" trong khi dòng meta ghi "20 câu", và nút Duyệt không bao giờ bật.
 *
 * Quy tắc: chỉ khoá khi đã thực sự nạp được cue. Chưa có cue thì cứ nạp lại mỗi lượt hỏi —
 * vô hại vì lúc đó admin chưa có gì để mất.
 */
export function shouldReloadEntries(
    loadedJobId: string | null,
    job: SubtitleJob | null
): { reload: boolean; nextLoadedId: string | null } {
    if (!job) return { reload: true, nextLoadedId: null };

    const hasEntries = Array.isArray(job.entries) && job.entries.length > 0;

    if (loadedJobId === job.id) return { reload: false, nextLoadedId: loadedJobId };

    return { reload: true, nextLoadedId: hasEntries ? job.id : null };
}

/** HH:MM:SS.mmm — định dạng mốc thời gian của chuẩn WebVTT (khác `formatCueTime` chỉ để hiển thị). */
function toVttTimestamp(seconds: number): string {
    const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = Math.floor(safe % 60);
    const ms = Math.round((safe - Math.floor(safe)) * 1000);
    const pad = (n: number, w = 2) => String(n).padStart(w, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
}

/**
 * Dựng nội dung WebVTT ngay trong trình duyệt từ bản admin ĐANG SỬA, để gắn vào thẻ <video>.
 *
 * Vì sao cần: màn duyệt mà chỉ liệt kê chữ bên cạnh thì người duyệt phải tự tưởng tượng phụ đề rơi
 * vào đâu trong phim. Có track thật thì sửa câu nào là thấy đổi ngay câu đó trên video — đó mới là
 * "duyệt". Không dùng file .vtt trên CDN vì file đó chỉ tồn tại SAU khi duyệt, và không phản ánh
 * những gì admin vừa gõ.
 */
export function buildVttFromEntries(entries: SubtitleEntry[] | null | undefined): string {
    if (!Array.isArray(entries) || entries.length === 0) return 'WEBVTT\n';

    let out = 'WEBVTT\n\n';
    for (const e of entries) {
        const text = typeof e?.text === 'string' ? e.text.trim() : '';
        const start = Number(e?.startTime);
        const end = Number(e?.endTime);
        if (!text || !Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;

        // Dòng trống là dấu ngắt cue của VTT — text nhiều dòng phải gộp lại kẻo vỡ cấu trúc.
        const safeText = text.replace(/\s*\n\s*/g, ' ');
        out += `${toVttTimestamp(start)} --> ${toVttTimestamp(end)}\n${safeText}\n\n`;
    }
    return out;
}

/** MM:SS.mmm — giá trị vô nghĩa trả về 00:00.000 thay vì để "NaN:NaN" lên màn hình. */
export function formatCueTime(seconds: number): string {
    const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
    const mins = Math.floor(safe / 60);
    const secs = Math.floor(safe % 60);
    const ms = Math.round((safe % 1) * 1000);
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}
